import React from 'react';

import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import {assertAndContinue} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BatchLinkProps, BatchSchemaBatchLinkProps} from 'constants/SampleConstants';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import {LookupConfigDescription, LookupConfigDescriptionProps} from 'lookup-config/LookupConfigDescription';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import {getFieldDefinition} from 'registry/field/FieldUtil';
import {
  getBoxSchemaSettingsUrl,
  getLocationSchemaSettingsUrl,
  getPlateSchemaSettingsUrl,
  getRegistryContainerSchemasUrl,
  getSchemaFieldSelectorSettingsUrl,
  getTagSchemaSettingsUrl,
} from 'registry/RegistryUtil';
import {assaySchemaURL} from 'results/assay-schemas/AssaySchemaDAO';
import {
  ComputedFieldType,
  DROPDOWN_FIELD_ICON,
  FieldType,
  isRequiredLinkAssaySchema,
  isRequiredLinkBatchSchema,
  isRequiredLinkFolderItemType,
  isRequiredLinkSchemaInterface,
  isRequiredLinkStorableSchema,
  isRequiredLinkTagSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import getSchemaInterfaceURL from 'schemas/schema-interface/v2/getSchemaInterfaceURL';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {UnitsQuery, UnitsQueryVariables} from 'units/__generated/UnitsQuery';
import {UNITS_ELLIPSIZE_LENGTH, UNITS_QUERY} from 'units/UnitsConstants';
import APIUtil from 'util/APIUtil';
import {capitalize, ellipsize, typeToName} from 'util/NameUtil';

import getArchiveAwareDisplayName from '../../archive/getArchiveAwareDisplayName';
import {displayPath} from './FieldPathUtil';

type MinimalField = Pick<
  SchemaField,
  'fieldType' | 'requiredLink' | 'schemaFieldSelectorId' | 'isSnapshot' | 'unitSymbol' | 'unitApiIdentifier'
>;

type MinimalFieldWithOptionalProperties = Partial<MinimalField> & {
  fieldType: FieldType;
};

export interface FieldDefinitionLabelProps {
  field: MinimalFieldWithOptionalProperties;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  lookupConfigDescriptionProps?: LookupConfigDescriptionProps;
  ownerUrlId?: string | null;
  registryId?: string | null;
  batchLinkProps?: BatchLinkProps;
  inEditMode?: boolean;
}

function getPossiblyLinkifiedLabel({
  thing,
  getNavUrl,
  ownerUrlId,
  registryId,
}: {
  thing: {id: string; name: string; archiveRecord?: ArchiveRecord | null};
  getNavUrl: (ownerUrlId: string, registryId: string, id: string) => string;
  ownerUrlId?: string | null;
  registryId?: string | null;
}): JSX.Element | string {
  if (registryId && ownerUrlId) {
    const url = getNavUrl(ownerUrlId, registryId, thing.id);
    return (
      <a href={url} onClick={BenchlingUtil.navigate}>
        {getArchiveAwareDisplayName(thing)}
      </a>
    );
  } else {
    return getArchiveAwareDisplayName(thing);
  }
}

function getDropdownIconAndLabel({
  field,
  schemaFieldSelectors,
  ownerUrlId,
  registryId,
}: {
  field: MinimalField;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  ownerUrlId?: string | null;
  registryId?: string | null;
}): {icon: BenchlingIconName; label: JSX.Element | string} {
  // Added to help diagnose BNCH-4361 (originally T8178)
  assertAndContinue(
    schemaFieldSelectors.filter((sfs) => sfs.id === field.schemaFieldSelectorId).length > 0,
    `Selected dropdown ${field.schemaFieldSelectorId} not found in loaded list of dropdowns`,
    {
      schemaFieldSelectorId: field.schemaFieldSelectorId,
      loadedSelectors: schemaFieldSelectors.map((value) => ({name: value.name, id: value.id})),
    }
  );

  const selector = schemaFieldSelectors.filter((sfs) => sfs.id === field.schemaFieldSelectorId)[0];
  return selector
    ? {
        icon: DROPDOWN_FIELD_ICON,
        label: getPossiblyLinkifiedLabel({
          thing: selector,
          getNavUrl: getSchemaFieldSelectorSettingsUrl,
          ownerUrlId,
          registryId,
        }),
      }
    : {
        icon: 'error',
        label: 'Insufficient access - need read permissions on previously selected dropdown',
      };
}

export function interfaceIdToIcon(interfaceId: string): BenchlingIconName {
  switch (interfaceId) {
    case ANTIBODY_SCHEMA_INTERFACE_ID:
      return 'antibody';
    case ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID:
    case ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID:
      return 'network-wired';
    case EQUIPMENT_SCHEMA_INTERFACE_ID:
      return 'microscope';
    default:
      return 'schema_interface';
  }
}

function getBioentityLinkIconAndLabel({
  field,
  ownerUrlId,
  registryId,
}: {
  field: MinimalField;
  ownerUrlId?: string | null;
  registryId?: string | null;
}): {icon: BenchlingIconName; label: JSX.Element | string} {
  if (isRequiredLinkFolderItemType(field.requiredLink)) {
    return {
      icon: field.requiredLink.folderItemType as BenchlingIconName,
      label: capitalize(typeToName(field.requiredLink.folderItemType)),
    };
  } else if (isRequiredLinkTagSchema(field.requiredLink)) {
    const {tagSchema: schema} = field.requiredLink;
    return {
      icon: schema.iconId,
      label: getPossiblyLinkifiedLabel({
        thing: schema,
        getNavUrl: getTagSchemaSettingsUrl,
        ownerUrlId,
        registryId,
      }),
    };
  } else if (isRequiredLinkSchemaInterface(field.requiredLink)) {
    const {schemaInterface} = field.requiredLink;
    const schemaInterfaceURL = getSchemaInterfaceURL(schemaInterface.id);
    return {
      icon: interfaceIdToIcon(schemaInterface.id),
      label:
        schemaInterfaceURL != null
          ? // Linkify label if there is a URL
            getPossiblyLinkifiedLabel({
              thing: schemaInterface,
              getNavUrl: (ownerUrlId, registryId, interfaceId: string) => schemaInterfaceURL,
              ownerUrlId: 'IGNORED',
              registryId: 'IGNORED',
            })
          : // Do NOT linkify label if there is no URL
            getArchiveAwareDisplayName(schemaInterface),
    };
  } else {
    return {
      icon: FolderItemIcon.BasicFolderItem,
      label: 'Any entity',
    };
  }
}

function getEntryLinkIconAndLabel({
  field,
  ownerUrlId,
  registryId,
}: {
  field: MinimalField;
  ownerUrlId?: string | null;
  registryId?: string | null;
}): {icon: BenchlingIconName; label: JSX.Element | string} {
  if (isRequiredLinkTagSchema(field.requiredLink)) {
    const {tagSchema: schema} = field.requiredLink;
    return {
      icon: schema.iconId,
      label: getPossiblyLinkifiedLabel({
        thing: schema,
        getNavUrl: getTagSchemaSettingsUrl,
        ownerUrlId,
        registryId,
      }),
    };
  } else {
    return {
      icon: FolderItemIcon.Entry,
      label: 'Entry',
    };
  }
}

function getPartLinkIconAndLabel({
  field,
  ownerUrlId,
  registryId,
}: {
  field: MinimalField;
  ownerUrlId?: string | null;
  registryId?: string | null;
}): {icon: BenchlingIconName; label: JSX.Element | string} {
  if (isRequiredLinkTagSchema(field.requiredLink)) {
    return {
      icon: 'part',
      label: (
        <>
          Part /{' '}
          {getPossiblyLinkifiedLabel({
            thing: field.requiredLink.tagSchema,
            getNavUrl: getTagSchemaSettingsUrl,
            ownerUrlId,
            registryId,
          })}
        </>
      ),
    };
  } else {
    return {
      icon: 'part',
      label: 'Part / Any sequence',
    };
  }
}

function getTranslationLinkIconAndLabel({
  field,
  ownerUrlId,
  registryId,
}: {
  field: MinimalField;
  ownerUrlId?: string | null;
  registryId?: string | null;
}): {icon: BenchlingIconName; label: JSX.Element | string} {
  if (isRequiredLinkTagSchema(field.requiredLink)) {
    return {
      icon: 'translation',
      label: (
        <>
          Translation /{' '}
          {getPossiblyLinkifiedLabel({
            thing: field.requiredLink.tagSchema,
            getNavUrl: getTagSchemaSettingsUrl,
            ownerUrlId,
            registryId,
          })}
        </>
      ),
    };
  } else {
    return {
      icon: 'translation',
      label: 'Translation / Any protein',
    };
  }
}

function getTranscriptionLinkIconAndLabel({
  field,
  ownerUrlId,
  registryId,
}: {
  field: MinimalField;
  ownerUrlId?: string | null;
  registryId?: string | null;
}): {icon: BenchlingIconName; label: JSX.Element | string} {
  if (isRequiredLinkTagSchema(field.requiredLink)) {
    return {
      icon: FolderItemIcon.RNASequence,
      label: (
        <>
          Transcription /{' '}
          {getPossiblyLinkifiedLabel({
            thing: field.requiredLink.tagSchema,
            getNavUrl: getTagSchemaSettingsUrl,
            ownerUrlId,
            registryId,
          })}
        </>
      ),
    };
  } else {
    return {
      icon: FolderItemIcon.RNASequence,
      label: 'Transcription / Any RNA Sequence',
    };
  }
}

function getAAPartLinkIconAndLabel({
  field,
  ownerUrlId,
  registryId,
}: {
  field: MinimalField;
  ownerUrlId?: string | null;
  registryId?: string | null;
}): {icon: BenchlingIconName; label: JSX.Element | string} {
  if (isRequiredLinkTagSchema(field.requiredLink)) {
    return {
      icon: FolderItemIcon.Protein,
      label: (
        <>
          AA Part /{' '}
          {getPossiblyLinkifiedLabel({
            thing: field.requiredLink.tagSchema,
            getNavUrl: getTagSchemaSettingsUrl,
            ownerUrlId,
            registryId,
          })}
        </>
      ),
    };
  } else {
    return {
      icon: 'part',
      label: 'Part / Any protein',
    };
  }
}

function getStorableLinkIconAndLabel({
  field,
  ownerUrlId,
  registryId,
}: {
  field: MinimalField;
  ownerUrlId?: string | null;
  registryId?: string | null;
}): {icon: BenchlingIconName; label: JSX.Element | string} {
  if (isRequiredLinkStorableSchema(field.requiredLink)) {
    const {storableSchema: schema} = field.requiredLink;
    const getNavUrl = APIUtil.isPlateSchema(schema.id)
      ? getPlateSchemaSettingsUrl
      : APIUtil.isContainerSchema(schema.id)
        ? (ownerUrlId: string, registryId: string, id: string) =>
            getRegistryContainerSchemasUrl(ownerUrlId, registryId)
        : APIUtil.isBoxSchema(schema.id)
          ? getBoxSchemaSettingsUrl
          : APIUtil.isLocationSchema(schema.id)
            ? getLocationSchemaSettingsUrl
            : null;
    assertAndContinue(
      getNavUrl != null,
      `Expected storable schema to be plate, container, box, or location. Id prefix of "${schema.id}" not recognized.`
    );
    const label =
      getNavUrl != null
        ? getPossiblyLinkifiedLabel({thing: schema, getNavUrl, registryId, ownerUrlId})
        : getArchiveAwareDisplayName(schema);
    return {
      icon: schema.iconId,
      label,
    };
  } else {
    return {icon: 'fridge', label: 'Any inventory'};
  }
}

function getResultLinkIconAndLabel({field}: {field: MinimalField}): {
  icon: BenchlingIconName;
  label: JSX.Element | string;
} {
  if (isRequiredLinkAssaySchema(field.requiredLink)) {
    const {assaySchema: schema} = field.requiredLink;
    return {
      icon: ModelTypeConstants.RESULT_SCHEMA,
      label: getPossiblyLinkifiedLabel({
        thing: schema,
        getNavUrl: (ownerUrlId, registryId, schemaId: string) =>
          assaySchemaURL(LegacyAssaySchemaType.ASSAY_RESULT, schemaId),
        registryId: 'IGNORED',
        ownerUrlId: 'IGNORED',
      }),
    };
  } else {
    return {icon: ModelTypeConstants.RESULT_SCHEMA, label: 'Any result'};
  }
}

function getRequestLinkIconAndLabel({field}: {field: MinimalField}): {
  icon: BenchlingIconName;
  label: JSX.Element | string;
} {
  if (isRequiredLinkAssaySchema(field.requiredLink)) {
    const {assaySchema: schema} = field.requiredLink;
    return {
      icon: ModelTypeConstants.REQUEST_SCHEMA,
      label: getPossiblyLinkifiedLabel({
        thing: schema,
        getNavUrl: (ownerUrlId, registryId, schemaId: string) =>
          assaySchemaURL(LegacyAssaySchemaType.ASSAY_REQUEST, schemaId),
        registryId: 'IGNORED',
        ownerUrlId: 'IGNORED',
      }),
    };
  } else {
    return {icon: ModelTypeConstants.REQUEST_SCHEMA, label: 'Any request'};
  }
}

function getRunLinkIconAndLabel({field}: {field: MinimalField}): {
  icon: BenchlingIconName;
  label: JSX.Element | string;
} {
  if (isRequiredLinkAssaySchema(field.requiredLink)) {
    const {assaySchema: schema} = field.requiredLink;
    return {
      icon: ModelTypeConstants.RUN_SCHEMA,
      label: getPossiblyLinkifiedLabel({
        thing: schema,
        getNavUrl: (ownerUrlId, registryId, schemaId: string) =>
          assaySchemaURL(LegacyAssaySchemaType.ASSAY_RUN, schemaId),
        registryId: 'IGNORED',
        ownerUrlId: 'IGNORED',
      }),
    };
  } else {
    return {icon: ModelTypeConstants.RUN_SCHEMA, label: 'Any run'};
  }
}

function getBatchLinkIconAndLabel({
  field,
  batchLinkProps,
}: {
  field: MinimalField;
  batchLinkProps?: BatchLinkProps;
}): {icon: BenchlingIconName; label: string} {
  if (isRequiredLinkBatchSchema(field.requiredLink)) {
    if (field.requiredLink.batchSchemaGraph != null) {
      return {
        icon: ModelTypeConstants.BATCH,
        label: displayPath({
          sourceTagSchemaId: (batchLinkProps as BatchSchemaBatchLinkProps).sourceTagSchemaId,
          tagSchemaFieldEdges: field.requiredLink.batchSchemaGraph.edges,
        }),
      };
    } else {
      const {batchSchema: schema} = field.requiredLink;
      return {
        icon: ModelTypeConstants.BATCH,
        label: getArchiveAwareDisplayName(schema),
      };
    }
  } else {
    return {icon: ModelTypeConstants.BATCH, label: 'Any batch'};
  }
}

function getUnitAwareLabel(field: MinimalField, fieldTypeLabel: string): string | JSX.Element {
  if (field.unitSymbol != null) {
    return `${fieldTypeLabel} (${ellipsize(field.unitSymbol, UNITS_ELLIPSIZE_LENGTH)})`;
  }
  if (field.unitApiIdentifier != null) {
    // In some cases, the field's unitSymbol is not populated (e.g. schema specs), so we need to query for it
    return (
      <BenchlingQuery<UnitsQuery, UnitsQueryVariables>
        query={UNITS_QUERY}
        variables={{ids: [field.unitApiIdentifier]}}
        renderLoading={() => fieldTypeLabel}
      >
        {(data) => `${fieldTypeLabel} (${ellipsize(data.units[0].symbol, UNITS_ELLIPSIZE_LENGTH)})`}
      </BenchlingQuery>
    );
  }
  return fieldTypeLabel;
}

export default function getIconAndLabel({
  field,
  schemaFieldSelectors,
  lookupConfigDescriptionProps,
  ownerUrlId,
  registryId,
  batchLinkProps,
}: FieldDefinitionLabelProps): {icon: BenchlingIconName; label: string | JSX.Element} {
  const minimalField: MinimalField = {
    fieldType: field.fieldType,
    requiredLink: field.requiredLink ?? null,
    schemaFieldSelectorId: field.schemaFieldSelectorId ?? null,
    isSnapshot: field.isSnapshot ?? false,
    unitSymbol: field.unitSymbol ?? null,
    unitApiIdentifier: field.unitApiIdentifier ?? null,
  };
  const fieldDefinition = getFieldDefinition(minimalField);
  switch (fieldDefinition) {
    case ComputedFieldType.SNAPSHOT:
      return {
        icon: 'function',
        label: lookupConfigDescriptionProps ? (
          <LookupConfigDescription {...lookupConfigDescriptionProps} />
        ) : (
          'Computed field (snapshot)'
        ),
      };
    case FieldType.TEXT:
      return {icon: 'text', label: 'Text'};
    case FieldType.LONG_TEXT:
      return {icon: 'text', label: 'Long text'};
    case FieldType.INTEGER:
      return {icon: 'number', label: getUnitAwareLabel(minimalField, 'Integer')};
    case FieldType.FLOAT:
      return {icon: 'number', label: getUnitAwareLabel(minimalField, 'Decimal')};
    case FieldType.DATE:
      return {icon: 'calendar', label: 'Date'};
    case FieldType.DATETIME:
      return {icon: 'calendar', label: 'Datetime'};
    case FieldType.BOOLEAN:
      return {icon: 'boolean', label: 'Boolean'};
    case FieldType.JSON:
      return {icon: 'json', label: 'JSON'};
    case FieldType.BLOB_LINK:
      return {icon: 'attachment', label: 'Attachment'};
    case FieldType.ENTRY_LINK:
      return getEntryLinkIconAndLabel({field: minimalField, ownerUrlId, registryId});
    case FieldType.SELECTOR:
      return getDropdownIconAndLabel({field: minimalField, schemaFieldSelectors, ownerUrlId, registryId});
    case FieldType.BIOENTITY_LINK:
      return getBioentityLinkIconAndLabel({field: minimalField, ownerUrlId, registryId});
    case FieldType.STORABLE_LINK:
      return getStorableLinkIconAndLabel({field: minimalField, ownerUrlId, registryId});
    case FieldType.ASSAY_RESULT_LINK:
      return getResultLinkIconAndLabel({field: minimalField});
    case FieldType.ASSAY_REQUEST_LINK:
      return getRequestLinkIconAndLabel({field: minimalField});
    case FieldType.ASSAY_RUN_LINK:
      return getRunLinkIconAndLabel({field: minimalField});
    case FieldType.BATCH_LINK:
      return getBatchLinkIconAndLabel({field: minimalField, batchLinkProps});
    case FieldType.PART_LINK:
      return getPartLinkIconAndLabel({field: minimalField, ownerUrlId, registryId});
    case FieldType.TRANSLATION_LINK:
      return getTranslationLinkIconAndLabel({field: minimalField, ownerUrlId, registryId});
    case FieldType.TRANSCRIPTION_LINK:
      return getTranscriptionLinkIconAndLabel({field: minimalField, ownerUrlId, registryId});
    case FieldType.AA_PART_LINK:
      return getAAPartLinkIconAndLabel({field: minimalField, ownerUrlId, registryId});
    default:
      throw new Error(`FieldDefinitionLabel not implemented for field definition ${fieldDefinition}`);
  }
}
